// A member QR code carries `provider.id/gymId[/childId]` — the keyboard-safe
// format, where the `|` of an Auth0 subject is written as `.` because a pipe is
// one of the characters a non-US keyboard layout mangles on the way through a
// hardware scanner. The older `provider|id:gymId[:childId]` form is still
// accepted: codes printed before the change are in members' photo rolls.
//
// Generated by `apps/member/src/features/checkin/CheckInHero.tsx` and
// `apps/member/src/features/family/FamilySection.tsx`.

export type ScanFormat = 'slash' | 'colon' | 'unknown'

export type ParsedScan =
  | {
      ok: true
      format: ScanFormat
      userId: string
      gymId: string
      childId?: string
      trimmed: boolean
      parts: number
    }
  | {
      ok: false
      reason: 'empty' | 'too-few-parts' | 'blank-segment'
      format: ScanFormat
      trimmed: boolean
      parts: number
    }

export function parseScanPayload(raw: string): ParsedScan {
  const payload = raw.trim()
  const trimmed = payload !== raw

  if (!payload) return { ok: false, reason: 'empty', format: 'unknown', trimmed, parts: 0 }

  const format: ScanFormat = payload.includes('/')
    ? 'slash'
    : payload.includes(':')
      ? 'colon'
      : 'unknown'

  const parts = payload.split(format === 'slash' ? '/' : ':')

  if (format === 'unknown' || parts.length < 2) {
    return { ok: false, reason: 'too-few-parts', format, trimmed, parts: parts.length }
  }

  // A truncated scan often keeps the separator but loses what follows it. That
  // is an unreadable code, not a code belonging to another gym, and saying so
  // is the difference between the staff re-scanning and them giving up.
  if (!parts[0] || !parts[1]) {
    return { ok: false, reason: 'blank-segment', format, trimmed, parts: parts.length }
  }

  return {
    ok: true,
    format,
    // The slash form encodes the subject's pipe as the first dot.
    userId: format === 'slash' ? parts[0].replace('.', '|') : parts[0],
    gymId: parts[1],
    childId: parts[2] || undefined,
    trimmed,
    parts: parts.length,
  }
}
